import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

marked.setOptions({
  breaks: true,
  gfm: true
})

const SUPPORT_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/></svg>'

export default class extends Controller {
  static targets = [
    "panel", "messages", "input", "sendBtn", "inputArea", "floatingStack", "topBtn",
    "escalateArea", "escalateName", "escalateEmail", "escalateMessage"
  ]
  static values = {
    conversationId: Number,
    msgGreeting: { type: String, default: "안녕하세요! 무엇이든 물어보세요." },
    msgSuggestionsLabel: { type: String, default: "추천 질문" },
    msgSuggestion1: { type: String, default: "이 서비스는 무엇인가요?" },
    msgSuggestion2: { type: String, default: "어떻게 시작하면 되나요?" },
    msgSuggestion3: { type: String, default: "가격 정책이 궁금해요" },
    msgErrorGeneric: { type: String, default: "오류가 발생했습니다. 잠시 후 다시 시도해주세요." },
    msgErrorNoResponse: { type: String, default: "응답을 받지 못했습니다." },
    msgErrorConnection: { type: String, default: "연결 오류가 발생했습니다. 다시 시도해주세요." },
    msgEscalateMissingFields: { type: String, default: "이름과 이메일을 입력해주세요." },
    msgEscalateSuccess: { type: String, default: "문의가 접수되었습니다." },
    msgEscalateError: { type: String, default: "오류가 발생했습니다. 다시 시도해주세요." }
  }

  connect() {
    this.suggestions = [
      this.msgSuggestion1Value,
      this.msgSuggestion2Value,
      this.msgSuggestion3Value
    ]
    this._fullscreen = false
    this._initialized = false
    this._handleScroll = () => this.updateTopButton()
    window.addEventListener("scroll", this._handleScroll, { passive: true })
    this.updateTopButton()
  }

  disconnect() {
    if (this._handleScroll) {
      window.removeEventListener("scroll", this._handleScroll)
    }
  }

  toggle() {
    const panel = this.panelTarget
    const isHidden = panel.classList.contains("hidden")
    panel.classList.toggle("hidden")

    if (isHidden && !this._initialized) {
      this._initialized = true
      this.initConversation()
    }
  }

  toggleFullscreen() {
    this._fullscreen = !this._fullscreen
    this.panelTarget.classList.toggle("fullscreen", this._fullscreen)
  }

  async initConversation() {
    try {
      const res = await fetch("/chatbot/conversation", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Content-Type": "application/json"
        }
      })
      const data = await res.json()
      this.conversationIdValue = data.conversation_id
    } catch (e) {
      // 연결 실패 시 대화 ID 없이 진행
    }

    this.renderSuggestions()
    this.appendMessage("assistant", this.msgGreetingValue)
  }

  renderSuggestions() {
    const container = document.createElement("div")
    container.className = "chatbot-widget__suggestions"

    const label = document.createElement("p")
    label.className = "chatbot-widget__suggestions-label"
    label.textContent = this.msgSuggestionsLabelValue
    container.appendChild(label)

    this.suggestions.forEach(text => {
      const btn = document.createElement("button")
      btn.className = "chatbot-widget__suggestion-btn"
      btn.textContent = text
      btn.dataset.suggestion = text
      btn.addEventListener("click", (e) => this.selectSuggestion(e))
      container.appendChild(btn)
    })

    this.messagesTarget.appendChild(container)
    this._suggestionsEl = container
  }

  selectSuggestion(e) {
    const suggestion = e.currentTarget.dataset.suggestion
    if (this._suggestionsEl) {
      this._suggestionsEl.remove()
      this._suggestionsEl = null
    }
    this.inputTarget.value = suggestion
    this.send()
  }

  handleKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      this.send()
    }
  }

  autoResize() {
    const el = this.inputTarget
    el.style.height = "38px"
    const newHeight = Math.min(el.scrollHeight, 100)
    el.style.height = newHeight + "px"
    el.style.overflowY = el.scrollHeight > 100 ? "auto" : "hidden"
  }

  async send() {
    const msg = this.inputTarget.value.trim()
    if (!msg) return
    if (this.sendBtnTarget.disabled) return

    // 추천 질문 제거
    if (this._suggestionsEl) {
      this._suggestionsEl.remove()
      this._suggestionsEl = null
    }

    this.inputTarget.value = ""
    this.inputTarget.style.height = "38px"
    this.appendMessage("user", msg)
    this.sendBtnTarget.disabled = true

    const typingEl = this.appendTyping()

    try {
      const res = await fetch("/chatbot/message", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: msg,
          conversation_id: this.conversationIdValue || null
        })
      })

      if (!res.ok) {
        typingEl.remove()
        this.appendMessage("assistant", this.msgErrorGenericValue)
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""
      let fullContent = ""
      let assistantEl = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop()
        for (const line of lines) {
          if (!line.startsWith("data:")) continue
          try {
            const parsed = JSON.parse(line.slice(5).trim())
            if (parsed.type === "content") {
              if (!assistantEl) {
                typingEl.remove()
                assistantEl = this.appendMessage("assistant", "")
              }
              fullContent += parsed.content
              assistantEl.innerHTML = marked.parse(fullContent)
              this.scrollToBottom()
            } else if (parsed.type === "error") {
              if (!assistantEl) {
                typingEl.remove()
                assistantEl = this.appendMessage("assistant", "")
              }
              fullContent = parsed.content || this.msgErrorGenericValue
              assistantEl.textContent = fullContent
            }
          } catch (_) {
            // JSON 파싱 실패 무시
          }
        }
      }

      if (!assistantEl) {
        typingEl.remove()
        this.appendMessage("assistant", this.msgErrorNoResponseValue)
      }
    } catch (e) {
      typingEl.remove()
      this.appendMessage("assistant", this.msgErrorConnectionValue)
    } finally {
      this.sendBtnTarget.disabled = false
    }
  }

  appendMessage(role, content) {
    if (role === "assistant") {
      const row = document.createElement("div")
      row.className = "chatbot-widget__msg-row"

      const icon = document.createElement("div")
      icon.className = "chatbot-widget__bot-icon"
      icon.innerHTML = SUPPORT_ICON_SVG

      const bubble = document.createElement("div")
      bubble.className = "chatbot-widget__msg-assistant"
      if (content) bubble.innerHTML = marked.parse(content)

      row.appendChild(icon)
      row.appendChild(bubble)
      this.messagesTarget.appendChild(row)
      this.scrollToBottom()
      return bubble
    } else {
      const div = document.createElement("div")
      div.className = "chatbot-widget__msg-user"
      div.textContent = content
      this.messagesTarget.appendChild(div)
      this.scrollToBottom()
      return div
    }
  }

  appendTyping() {
    const row = document.createElement("div")
    row.className = "chatbot-widget__msg-row"

    const icon = document.createElement("div")
    icon.className = "chatbot-widget__bot-icon chatbot-widget__bot-icon--loading"
    icon.innerHTML = SUPPORT_ICON_SVG

    row.appendChild(icon)
    this.messagesTarget.appendChild(row)
    this.scrollToBottom()
    return row
  }

  showEscalate() {
    this.inputAreaTarget.classList.add("hidden")
    this.escalateAreaTarget.classList.remove("hidden")
  }

  hideEscalate() {
    this.escalateAreaTarget.classList.add("hidden")
    this.inputAreaTarget.classList.remove("hidden")
  }

  async submitEscalate() {
    const name = this.escalateNameTarget.value.trim()
    const email = this.escalateEmailTarget.value.trim()
    const message = this.escalateMessageTarget.value.trim()

    if (!name || !email) {
      alert(this.msgEscalateMissingFieldsValue)
      return
    }

    try {
      const res = await fetch("/chatbot/escalate", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name,
          email,
          message,
          conversation_id: this.conversationIdValue || null
        })
      })

      const data = await res.json()
      this.hideEscalate()
      this.appendMessage("assistant", data.message || this.msgEscalateSuccessValue)
    } catch (e) {
      alert(this.msgEscalateErrorValue)
    }
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  updateTopButton() {
    if (!this.hasTopBtnTarget) return

    const isVisible = window.scrollY > 320
    this.topBtnTarget.classList.toggle("is-visible", isVisible)
    if (this.hasFloatingStackTarget) {
      this.floatingStackTarget.classList.toggle("is-top-visible", isVisible)
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
